import { Loader2 } from "lucide-react";
import type { ReactNode } from "react";

export interface Column<T> {
  header: string;
  render: (row: T) => ReactNode;
  className?: string;
  width?: string;
}

interface Props<T> {
  columns: Column<T>[];
  rows: T[] | undefined;
  loading?: boolean;
  empty?: ReactNode;
  getKey: (row: T) => string | number;
  onRowClick?: (row: T) => void;
}

export function DataTable<T>({
  columns,
  rows,
  loading,
  empty,
  getKey,
  onRowClick,
}: Props<T>) {
  if (loading) {
    return (
      <div className="card p-12 flex items-center justify-center text-slate-400">
        <Loader2 className="animate-spin" size={20} />
      </div>
    );
  }
  if (!rows || rows.length === 0) {
    return (
      <div className="card p-12 text-center text-sm text-slate-500">
        {empty ?? "Sin resultados"}
      </div>
    );
  }
  return (
    <div className="card overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/60">
            {columns.map((c, i) => (
              <th
                key={i}
                className={`text-left px-4 py-2.5 font-medium text-xs uppercase tracking-wide text-slate-500 ${c.className ?? ""}`}
                style={c.width ? { width: c.width } : undefined}
              >
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={getKey(row)}
              onClick={() => onRowClick?.(row)}
              className={`border-b border-slate-100 dark:border-slate-800 table-row-hover ${onRowClick ? "cursor-pointer" : ""}`}
            >
              {columns.map((c, i) => (
                <td key={i} className={`px-4 py-2.5 align-middle ${c.className ?? ""}`}>
                  {c.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
